import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OPEN_DRAFT_DOCUMENT } from '../common/OpenDraftExistsDialog';
import { findOpenEstimateDraftApi } from '../services/business/estimates/estimates.api';
import { findOpenInvoiceDraftApi } from '../services/business/invoices/invoices.api';
import { findOpenWorkOrderDraftApi } from '../services/business/workOrders/workOrders.api';

/** One lookup per document type — the backend decides which drafts match. */
const FIND_OPEN_DRAFT_API = {
    estimate: findOpenEstimateDraftApi,
    invoice: findOpenInvoiceDraftApi,
    workOrder: findOpenWorkOrderDraftApi,
};

const joinName = (person) => person?.fullName || [person?.firstName, person?.lastName].filter(Boolean).join(' ');

const vehicleTitle = (vehicle) => [vehicle?.year || vehicle?.vehicleYear, vehicle?.vehicleMake?.name, vehicle?.vehicleModel?.name].filter(Boolean).join(' ');

/**
 * Maps the lookup's `draft` onto what the dialog shows:
 * { _id, invoiceNumber | estimateNumber | workOrderNumber, invoiceDate | estimateDate | workOrderDate,
 *   customer: { firstName, lastName, fullName }, vehicle: { year, vehicleMake, vehicleModel, … } }
 */
const toDialogDraft = (draft) => {
    const date = draft?.invoiceDate || draft?.estimateDate || draft?.workOrderDate || draft?.createdAt;

    return {
        id: draft?._id,
        customerName: joinName(draft?.customer),
        documentNumber: draft?.invoiceNumber || draft?.estimateNumber || draft?.workOrderNumber || '',
        vehicle: vehicleTitle(draft?.vehicle),
        date: date ? dayjs(date).format('DD-MMM-YYYY') : '',
    };
};

/**
 * "Open draft already exists" alert for the new estimate / invoice / work order editors.
 *
 * Once a vehicle is selected, asks the document's API whether a draft already exists for that
 * vehicle (`{ vehicleId }`). Picking the customer alone never asks. Each vehicle is asked once, so
 * dismissing the alert does not bring it back until another vehicle is picked.
 *
 * `enabled` must be false for every flow other than a plain new document or one started from the
 * customer profile (edit, duplicate, conversions, appointment, estimate request …).
 */
const useOpenDraftAlert = ({ documentType, enabled, vehicle }) => {
    const navigate = useNavigate();
    const [openDraftModal, setOpenDraftModal] = useState(false);
    const [openDraftData, setOpenDraftData] = useState(null);
    const lastCheckedVehicleRef = useRef(null);

    const vehicleId = vehicle?._id || '';

    useEffect(() => {
        if (!enabled) return;
        // No vehicle selected — nothing to match; the next pick asks again.
        if (!vehicleId) {
            lastCheckedVehicleRef.current = null;
            return;
        }
        if (lastCheckedVehicleRef.current === vehicleId) return;
        lastCheckedVehicleRef.current = vehicleId;

        FIND_OPEN_DRAFT_API[documentType]({ vehicleId })
            .then((draft) => {
                // The vehicle changed while the request was in flight.
                if (lastCheckedVehicleRef.current !== vehicleId || !draft?._id) return;
                setOpenDraftData(toDialogDraft(draft));
                setOpenDraftModal(true);
            })
            .catch((error) => console.log(error));
    }, [enabled, documentType, vehicleId]);

    const handleCloseOpenDraftModal = useCallback(() => {
        setOpenDraftModal(false);
        setOpenDraftData(null);
    }, []);

    const handleViewOpenDraft = useCallback(() => {
        const target = openDraftData?.id && OPEN_DRAFT_DOCUMENT[documentType]?.detailPath(openDraftData.id);
        handleCloseOpenDraftModal();
        if (target) navigate(target);
    }, [openDraftData, documentType, navigate, handleCloseOpenDraftModal]);

    return { openDraftModal, openDraftData, handleCloseOpenDraftModal, handleViewOpenDraft };
};

export default useOpenDraftAlert;
